import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import Spinner from 'react-bootstrap/Spinner';
import Badge from 'react-bootstrap/Badge';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';

import {
    FaPlus,
    FaTrash,
} from 'react-icons/fa';
import {
    FiDatabase, FiFileText, FiGlobe, FiLock, FiInfo,
    FiPlus, FiTrash2, FiArrowRight,
} from 'react-icons/fi';

import toast from 'react-hot-toast';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/StAuthGiver';
import { useCms } from '../lib/CmsContext';
import { callApi, getCurSiteId } from '../lib/Common_funcs.js';

const WhichEnv = (process.env.REACT_APP_TURBO_ENV === undefined ? 'aws' : 'staging');
const Tdebug = (WhichEnv !== 'aws');

// Available field types
const FIELD_TYPES = [
    { value: 'text',        label: 'Text',          description: 'Short text, titles, names' },
    { value: 'richtext',    label: 'Rich Text',      description: 'Long content with formatting (Markdown)' },
    { value: 'number',      label: 'Number',         description: 'Integer or decimal values' },
    { value: 'boolean',     label: 'Boolean',        description: 'Yes/No toggle' },
    { value: 'date',        label: 'Date',           description: 'Date picker' },
    { value: 'datetime',    label: 'Date & Time',    description: 'Date and time picker' },
    { value: 'media',       label: 'Media',          description: 'Single image or gallery' },
    { value: 'select',      label: 'Select',         description: 'Single choice — dropdown or radio' },
    { value: 'multiselect', label: 'Multi Select',   description: 'Multiple choices — tags or checkboxes' },
    { value: 'slug',        label: 'Slug',           description: 'URL-friendly identifier' },
    // { value: 'reference',   label: 'Reference',      description: 'Link to another entry' },
];

const SELECT_UI_OPTIONS    = ['dropdown', 'radio'];
const MULTISELECT_UI_OPTIONS = ['tags', 'checkbox'];

export const StCmsSchemas = () => {
    const { loginAction, logOut } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Use shared CMS context for schemas
    const { schemas, schemasLoading: loading, schemasRefreshing, refreshSchemas } = useCms();
    const [siteId] = useState(getCurSiteId() || '');

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedSchema, setSelectedSchema] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    // Create form state
    const [newSchemaName, setNewSchemaName] = useState('');
    const [newSchemaFields, setNewSchemaFields] = useState([
        { name: 'title', type: 'text', required: true, max_length: 200 },
        { name: 'slug', type: 'slug', source_field: 'title', required: true },
    ]);

    // Options input per field index (for select/radio/checkbox)
    const [optionInputs, setOptionInputs] = useState({});

    // AI Assist state
    const [showAiPanel, setShowAiPanel] = useState(false);
    const [aiPrompt, setAiPrompt] = useState('');
    const [aiGenerating, setAiGenerating] = useState(false);

    // Refresh schemas every time this page mounts so entry counts are current
    useEffect(() => {
        refreshSchemas();
    }, []);

    // Keep selectedSchema in sync with fresh schemas data after a refresh
    useEffect(() => {
        if (selectedSchema) {
            const fresh = schemas.find(s => s.schema_id === selectedSchema.schema_id);
            if (fresh) setSelectedSchema(fresh);
        }
    }, [schemas]);

    // Auto-open create modal if ?create=true query param is present
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('create') === 'true') {
            setShowCreateModal(true);
            navigate(location.pathname, { replace: true });
        }
    }, [location.search, navigate, location.pathname]);

    const createSchema = async () => {
        if (!newSchemaName.trim()) {
            toast.error('Please enter a name for the content type');
            return;
        }

        if (newSchemaFields.length < 2) {
            toast.error('At least 2 fields are required');
            return;
        }

        try {
            setActionLoading(true);
            const url = `/sites/${siteId}/cms/schemas`;
            const requestBody = {
                display_name: newSchemaName.trim(),
                fields: newSchemaFields,
                schema_mode: 'custom'
            };

            const rcJson = await callApi(url, 'POST', {}, JSON.stringify(requestBody));

            if (rcJson.disposition === 'logout') {
                logOut();
                return;
            }

            if (rcJson.httpStatus === '200' || rcJson.httpStatus === 200 || rcJson.httpStatus === '201' || rcJson.httpStatus === 201) {
                toast.success('Content type created successfully');
                setShowCreateModal(false);
                resetCreateForm();
                refreshSchemas();

                if (rcJson.respBody?.etac) {
                    loginAction(null, rcJson.respBody.etac);
                }
            } else {
                // Error may be in respBody.error.message or respBody.message
                const errMsg = rcJson.respBody?.error?.message || rcJson.message || 'Failed to create content type';
                toast.error(errMsg);
            }
        } catch (err) {
            Tdebug && console.log(err);
            toast.error('Failed to create content type');
        } finally {
            setActionLoading(false);
        }
    };

    const deleteSchema = async () => {
        if (!selectedSchema) return;

        try {
            setActionLoading(true);
            const url = `/sites/${siteId}/cms/schemas/${selectedSchema.schema_id}`;
            const rcJson = await callApi(url, 'DELETE', {}, JSON.stringify({}));

            if (rcJson.disposition === 'logout') {
                logOut();
                return;
            }

            if (rcJson.httpStatus === '200' || rcJson.httpStatus === 200) {
                toast.success('Content type deleted successfully');
                setShowDeleteModal(false);
                setSelectedSchema(null);
                refreshSchemas();
            } else {
                // Error may be in respBody.error.message or respBody.message
                const errMsg = rcJson.respBody?.error?.message || rcJson.message || 'Failed to delete content type';
                toast.error(errMsg);
            }
        } catch (err) {
            Tdebug && console.log(err);
            toast.error('Failed to delete content type');
        } finally {
            setActionLoading(false);
        }
    };

    const resetCreateForm = () => {
        setNewSchemaName('');
        setNewSchemaFields([
            { name: 'title', type: 'text', required: true, max_length: 200 },
            { name: 'slug', type: 'slug', source_field: 'title', required: true },
        ]);
        setOptionInputs({});
        setShowAiPanel(false);
        setAiPrompt('');
        setAiGenerating(false);
    };

    const addField = () => {
        if (newSchemaFields.length >= 20) {
            toast.error('Maximum 20 fields allowed');
            return;
        }
        setNewSchemaFields([
            ...newSchemaFields,
            { name: '', type: 'text', required: false }
        ]);
    };

    const updateField = (index, key, value) => {
        const updated = [...newSchemaFields];
        updated[index] = { ...updated[index], [key]: value };
        setNewSchemaFields(updated);
    };

    const removeField = (index) => {
        if (newSchemaFields.length <= 2) {
            toast.error('At least 2 fields are required');
            return;
        }
        setNewSchemaFields(newSchemaFields.filter((_, i) => i !== index));
    };

    const addOption = (fieldIndex) => {
        const val = (optionInputs[fieldIndex] || '').trim();
        if (!val) return;
        const current = newSchemaFields[fieldIndex].options || [];
        if (current.includes(val)) {
            toast.error('Option already exists');
            return;
        }
        updateField(fieldIndex, 'options', [...current, val]);
        setOptionInputs(prev => ({ ...prev, [fieldIndex]: '' }));
    };

    const removeOption = (fieldIndex, option) => {
        const current = newSchemaFields[fieldIndex].options || [];
        updateField(fieldIndex, 'options', current.filter(o => o !== option));
    };

    // AI Schema Templates
    const AI_TEMPLATES = {
        events: {
            name: 'Events',
            prompt: 'Events with title, description, start date, end date, location, ticket price, capacity, featured image, and category',
            fields: [
                { name: 'title', type: 'text', required: true, max_length: 200 },
                { name: 'slug', type: 'slug', source_field: 'title', required: true },
                { name: 'description', type: 'richtext', required: true },
                { name: 'start_date', type: 'datetime', required: true },
                { name: 'end_date', type: 'datetime', required: false },
                { name: 'location', type: 'text', required: true },
                { name: 'ticket_price', type: 'number', required: false },
                { name: 'capacity', type: 'number', required: false },
                { name: 'featured_image', type: 'media', required: false },
                { name: 'category', type: 'select', required: true }
            ]
        },
        portfolio: {
            name: 'Portfolio',
            prompt: 'Portfolio projects with title, client name, description, technologies used, project URL, featured image, completion date',
            fields: [
                { name: 'title', type: 'text', required: true, max_length: 200 },
                { name: 'slug', type: 'slug', source_field: 'title', required: true },
                { name: 'client_name', type: 'text', required: false },
                { name: 'description', type: 'richtext', required: true },
                { name: 'technologies', type: 'multiselect', required: false },
                { name: 'project_url', type: 'text', required: false },
                { name: 'featured_image', type: 'media', required: true },
                { name: 'completion_date', type: 'date', required: false }
            ]
        },
        testimonials: {
            name: 'Testimonials',
            prompt: 'Customer testimonials with author name, company, role, quote, rating, and photo',
            fields: [
                { name: 'author_name', type: 'text', required: true, max_length: 100 },
                { name: 'slug', type: 'slug', source_field: 'author_name', required: true },
                { name: 'company', type: 'text', required: false },
                { name: 'role', type: 'text', required: false },
                { name: 'quote', type: 'richtext', required: true },
                { name: 'rating', type: 'number', required: false },
                { name: 'photo', type: 'media', required: false }
            ]
        },
        faq: {
            name: 'FAQ',
            prompt: 'Frequently asked questions with question, answer, category, and display order',
            fields: [
                { name: 'question', type: 'text', required: true, max_length: 300 },
                { name: 'slug', type: 'slug', source_field: 'question', required: true },
                { name: 'answer', type: 'richtext', required: true },
                { name: 'category', type: 'select', required: false },
                { name: 'display_order', type: 'number', required: false }
            ]
        },
        team: {
            name: 'Team Members',
            prompt: 'Team members with name, role, bio, email, photo, and social links',
            fields: [
                { name: 'name', type: 'text', required: true, max_length: 100 },
                { name: 'slug', type: 'slug', source_field: 'name', required: true },
                { name: 'role', type: 'text', required: true },
                { name: 'bio', type: 'richtext', required: false },
                { name: 'email', type: 'text', required: false },
                { name: 'photo', type: 'media', required: false },
                { name: 'linkedin_url', type: 'text', required: false }
            ]
        },
        products: {
            name: 'Products',
            prompt: 'Products with name, description, price, category, SKU, stock status, and images',
            fields: [
                { name: 'name', type: 'text', required: true, max_length: 200 },
                { name: 'slug', type: 'slug', source_field: 'name', required: true },
                { name: 'description', type: 'richtext', required: true },
                { name: 'price', type: 'number', required: true },
                { name: 'category', type: 'select', required: true },
                { name: 'sku', type: 'text', required: false },
                { name: 'in_stock', type: 'boolean', required: true },
                { name: 'featured_image', type: 'media', required: false }
            ]
        }
    };

    const applyAiTemplate = (templateKey) => {
        const template = AI_TEMPLATES[templateKey];
        if (template) {
            setAiPrompt(template.prompt);
            generateFromTemplate(template);
        }
    };

    const generateFromTemplate = (template) => {
        setAiGenerating(true);
        setTimeout(() => {
            setNewSchemaName(template.name);
            setNewSchemaFields(template.fields);
            setAiGenerating(false);
            setShowAiPanel(false);
            toast.success('Fields generated! Review and customize as needed.');
        }, 800);
    };

    const generateSchemaFromPrompt = () => {
        if (!aiPrompt.trim()) {
            toast.error('Please describe your content type');
            return;
        }

        setAiGenerating(true);
        const lowerPrompt = aiPrompt.toLowerCase();

        setTimeout(() => {
            let generatedFields = [
                { name: 'title', type: 'text', required: true, max_length: 200 },
                { name: 'slug', type: 'slug', source_field: 'title', required: true }
            ];
            let suggestedName = 'Custom Content';

            // Detect content type from prompt
            for (const [key, template] of Object.entries(AI_TEMPLATES)) {
                if (lowerPrompt.includes(key) || lowerPrompt.includes(template.name.toLowerCase())) {
                    setNewSchemaName(template.name);
                    setNewSchemaFields(template.fields);
                    setAiGenerating(false);
                    setShowAiPanel(false);
                    toast.success('Fields generated! Review and customize as needed.');
                    return;
                }
            }

            // Generic field detection based on keywords
            if (lowerPrompt.includes('description') || lowerPrompt.includes('content')) {
                generatedFields.push({ name: 'description', type: 'richtext', required: false });
            }
            if (lowerPrompt.includes('image') || lowerPrompt.includes('photo') || lowerPrompt.includes('picture')) {
                generatedFields.push({ name: 'featured_image', type: 'media', required: false });
            }
            if (lowerPrompt.includes('date')) {
                generatedFields.push({ name: 'date', type: 'date', required: false });
            }
            if (lowerPrompt.includes('price') || lowerPrompt.includes('cost')) {
                generatedFields.push({ name: 'price', type: 'number', required: false });
            }
            if (lowerPrompt.includes('category') || lowerPrompt.includes('type')) {
                generatedFields.push({ name: 'category', type: 'select', required: false });
            }
            if (lowerPrompt.includes('tag')) {
                generatedFields.push({ name: 'tags', type: 'multiselect', required: false });
            }
            if (lowerPrompt.includes('url') || lowerPrompt.includes('link')) {
                generatedFields.push({ name: 'url', type: 'text', required: false });
            }
            if (lowerPrompt.includes('email')) {
                generatedFields.push({ name: 'email', type: 'text', required: false });
            }
            if (lowerPrompt.includes('active') || lowerPrompt.includes('published') || lowerPrompt.includes('visible')) {
                generatedFields.push({ name: 'is_active', type: 'boolean', required: true });
            }

            // Extract potential name from prompt
            const nameMatch = aiPrompt.match(/content type for (.+?)(?:\s+with|\s+that|\.|,|$)/i);
            if (nameMatch) {
                suggestedName = nameMatch[1].trim().replace(/^(a|an|the)\s+/i, '');
                suggestedName = suggestedName.charAt(0).toUpperCase() + suggestedName.slice(1);
            }

            setNewSchemaName(suggestedName);
            setNewSchemaFields(generatedFields);
            setAiGenerating(false);
            setShowAiPanel(false);
            toast.success('Fields generated! Review and customize as needed.');
        }, 1000);
    };

    const getSchemaIcon = (schemaId, isSystem) => {
        if (schemaId === 'blog') return <FiFileText size={16} style={{ color: 'var(--accent-primary)' }} />;
        if (schemaId === 'social_post') return <FiGlobe size={16} style={{ color: 'var(--accent-primary)' }} />;
        return <FiDatabase size={16} style={{ color: 'var(--text-secondary)' }} />;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const getSchemaRoute = (schemaId) => {
        if (schemaId === 'blog') return `/${siteId}/cms/blog`;
        return `/${siteId}/cms/entries/${schemaId}`;
    };


    return (
        <div className="app-content animate-in">
            {/* Header */}
            <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                    <h1 className="dashboard-title"><FiDatabase size={20} /> Content Types</h1>
                    <p className="dashboard-subtitle">Manage your CMS content types</p>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 18px', background: 'var(--accent-gradient)', color: 'white', border: 'none', borderRadius: 'var(--radius-md)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}
                >
                    <FiPlus size={14} /> New Content Type
                </button>
            </div>

            {/* Info banner */}
            <div style={{
                display: 'flex', alignItems: 'flex-start', gap: '10px',
                background: 'var(--accent-soft)', border: '1px solid rgba(37,99,235,0.15)',
                borderRadius: 'var(--radius-md)', padding: '12px 16px', marginBottom: '24px',
                fontSize: '13px', color: 'var(--text-secondary)',
            }}>
                <FiInfo size={16} style={{ color: 'var(--accent-primary)', flexShrink: 0, marginTop: '1px' }} />
                <span>Content types define the structure of your content. System types (Blog, Social Post) cannot be modified or deleted.</span>
            </div>

            {/* Schemas Table */}
            <div className="bento-card" style={{ padding: 0, overflow: 'hidden' }}>
                {schemas.length === 0 && !loading && !schemasRefreshing ? (
                    <div style={{ textAlign: 'center', padding: '48px 20px' }}>
                        <FiDatabase size={36} style={{ color: 'var(--text-tertiary)', opacity: 0.4, marginBottom: '12px' }} />
                        <p style={{ fontSize: '14px', fontWeight: 500, marginBottom: '4px', color: 'var(--text-primary)' }}>No Content Types</p>
                        <p style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginBottom: '16px' }}>Create your first content type to get started.</p>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 16px', background: 'var(--accent-gradient)', color: 'white', border: 'none', borderRadius: 'var(--radius-md)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}
                        >
                            <FiPlus size={14} /> Create Content Type
                        </button>
                    </div>
                ) : (schemasRefreshing || loading) ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '48px', minHeight: '180px' }}>
                        <Spinner animation="border" style={{ width: '1.5rem', height: '1.5rem', color: 'var(--accent-primary)' }} />
                        <span style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>{schemasRefreshing ? 'Fetching latest schemas...' : 'Loading...'}</span>
                    </div>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                {['Name', 'Fields', 'Created', 'Actions'].map((h, i) => (
                                    <th key={h} style={{ padding: '11px 20px', textAlign: i === 1 ? 'center' : i === 3 ? 'right' : 'left', fontSize: '11px', fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {schemas.map((schema) => (
                                <tr key={schema.schema_id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                    <td style={{ padding: '12px 20px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <div style={{ width: '32px', height: '32px', borderRadius: 'var(--radius-sm)', background: 'var(--bg-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                {getSchemaIcon(schema.schema_id, schema.is_system)}
                                            </div>
                                            <div>
                                                <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>{schema.display_name}</div>
                                                <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{schema.schema_id}</div>
                                            </div>
                                            {schema.is_system && (
                                                <span className="badge-modern" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: '4px' }}>
                                                    <FiLock size={9} /> System
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td style={{ padding: '12px 20px', textAlign: 'center' }}>
                                        <span className="badge-modern" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>{schema.fields?.length || 0}</span>
                                    </td>
                                    <td style={{ padding: '12px 20px', fontSize: '13px', color: 'var(--text-tertiary)' }}>
                                        {schema.is_system ? 'Built-in' : formatDate(schema.created_at)}
                                    </td>
                                    <td style={{ padding: '12px 20px', textAlign: 'right' }}>
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '4px' }}>
                                            <button
                                                title="View Details"
                                                onClick={() => { setSelectedSchema(schema); setShowViewModal(true); }}
                                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', borderRadius: 'var(--radius-sm)' }}
                                            >
                                                <FiInfo size={14} />
                                            </button>
                                            {getSchemaRoute(schema.schema_id) && (
                                                <button
                                                    title="View Entries"
                                                    onClick={() => navigate(getSchemaRoute(schema.schema_id))}
                                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', border: 'none', background: 'transparent', color: 'var(--accent-primary)', cursor: 'pointer', borderRadius: 'var(--radius-sm)' }}
                                                >
                                                    <FiArrowRight size={14} />
                                                </button>
                                            )}
                                            {!schema.is_system && (
                                                <button
                                                    title="Delete"
                                                    onClick={() => { setSelectedSchema(schema); setShowDeleteModal(true); }}
                                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', border: 'none', background: 'transparent', color: 'var(--error)', cursor: 'pointer', borderRadius: 'var(--radius-sm)' }}
                                                >
                                                    <FiTrash2 size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Create Schema Modal */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" centered dialogClassName="modern-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Create Content Type</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {/* AI feature — coming soon

                    <div
                        className="d-flex align-items-center gap-2 p-2 mb-3 rounded"
                        style={{
                            background: 'linear-gradient(135deg, #eff6ff 0%, #f5f3ff 100%)',
                            border: '1px solid #c7d2fe'
                        }}
                    >
                        <FaMagic className="text-purple" style={{ color: '#7c3aed' }} />
                        <span className="flex-grow-1" style={{ fontSize: '12px', color: '#5b21b6' }}>
                            Describe what you need and AI will generate the fields for you.
                        </span>
                        <Button
                            size="sm"
                            onClick={() => setShowAiPanel(!showAiPanel)}
                            style={{
                                background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                                border: 'none',
                                fontSize: '11px'
                            }}
                        >
                            <FaMagic className="me-1" /> AI Assist
                        </Button>
                    </div>

                    {showAiPanel && (
                        <div
                            className="p-3 mb-3 rounded"
                            style={{
                                background: '#faf5ff',
                                border: '1px solid #e9d5ff'
                            }}
                        >
                            <h6 className="mb-2 d-flex align-items-center gap-2" style={{ color: '#7c3aed', fontSize: '12px' }}>
                                <FaMagic /> AI Schema Generator
                            </h6>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                placeholder="e.g., I need a data type for restaurant menu items with name, description, price, category, image, and dietary tags..."
                                value={aiPrompt}
                                onChange={(e) => setAiPrompt(e.target.value)}
                                className="mb-2"
                                style={{ fontSize: '13px', border: '1px solid #e9d5ff' }}
                            />
                            <div className="d-flex flex-wrap gap-1 mb-2">
                                <span style={{ fontSize: '11px', color: '#6b7280' }}>Quick templates:</span>
                                {Object.entries(AI_TEMPLATES).map(([key, template]) => (
                                    <Button
                                        key={key}
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => applyAiTemplate(key)}
                                        style={{
                                            fontSize: '11px',
                                            padding: '2px 8px',
                                            borderColor: '#e9d5ff',
                                            color: '#6b21a8'
                                        }}
                                        disabled={aiGenerating}
                                    >
                                        {template.name}
                                    </Button>
                                ))}
                            </div>
                            <div className="d-flex justify-content-end">
                                <Button
                                    size="sm"
                                    onClick={generateSchemaFromPrompt}
                                    disabled={aiGenerating || !aiPrompt.trim()}
                                    style={{
                                        background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                                        border: 'none',
                                        fontSize: '11px'
                                    }}
                                >
                                    {aiGenerating ? (
                                        <>
                                            <Spinner size="sm" className="me-1" />
                                            Generating...
                                        </>
                                    ) : (
                                        <>
                                            <FaMagic className="me-1" /> Generate Fields
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    )}

                    {aiGenerating && !showAiPanel && (
                        <div
                            className="d-flex align-items-center gap-2 p-3 mb-3 rounded"
                            style={{ background: '#faf5ff', border: '1px solid #e9d5ff', color: '#7c3aed' }}
                        >
                            <Spinner size="sm" />
                            <span style={{ fontSize: '13px' }}>AI is generating fields...</span>
                        </div>
                    )}

                    */}

                    <Form.Group className="mb-4">
                        <Form.Label className="fw-semibold">Name *</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="e.g., Product, Testimonial, FAQ"
                            value={newSchemaName}
                            onChange={(e) => setNewSchemaName(e.target.value)}
                        />
                        <Form.Text className="text-muted">
                            A human-readable name for this content type.
                        </Form.Text>
                    </Form.Group>

                    <div className="mb-3 d-flex justify-content-between align-items-center">
                        <Form.Label className="fw-semibold mb-0">Fields ({newSchemaFields.length}/20)</Form.Label>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={addField}
                            disabled={newSchemaFields.length >= 20}
                        >
                            <FaPlus className="me-1" /> Add Field
                        </Button>
                    </div>

                    <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                        {newSchemaFields.map((field, index) => (
                            <Card key={index} className="mb-2">
                                <Card.Body className="py-2 px-3">
                                    <Row className="align-items-center g-2">
                                        <Col sm={4}>
                                            <Form.Control
                                                type="text"
                                                size="sm"
                                                placeholder="Field name"
                                                value={field.name}
                                                onChange={(e) => updateField(index, 'name', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                                            />
                                        </Col>
                                        <Col sm={4}>
                                            <Form.Select
                                                size="sm"
                                                value={field.type}
                                                onChange={(e) => {
                                                    const newType = e.target.value;
                                                    const updated = [...newSchemaFields];
                                                    updated[index] = {
                                                        ...updated[index],
                                                        type: newType,
                                                        ui: newType === 'select' ? 'dropdown' : newType === 'multiselect' ? 'tags' : undefined,
                                                        options: undefined,
                                                        multiple: undefined,
                                                    };
                                                    setNewSchemaFields(updated);
                                                }}
                                            >
                                                {FIELD_TYPES.map((ft) => (
                                                    <option key={ft.value} value={ft.value}>
                                                        {ft.label}
                                                    </option>
                                                ))}
                                            </Form.Select>

                                            {/* UI sub-type selector */}
                                            {field.type === 'select' && (
                                                <Form.Select
                                                    size="sm"
                                                    className="mt-1"
                                                    value={field.ui || 'dropdown'}
                                                    onChange={(e) => updateField(index, 'ui', e.target.value)}
                                                    style={{ fontSize: '11px' }}
                                                >
                                                    <option value="dropdown">Dropdown</option>
                                                    <option value="radio">Radio Buttons</option>
                                                </Form.Select>
                                            )}
                                            {field.type === 'multiselect' && (
                                                <Form.Select
                                                    size="sm"
                                                    className="mt-1"
                                                    value={field.ui || 'tags'}
                                                    onChange={(e) => updateField(index, 'ui', e.target.value)}
                                                    style={{ fontSize: '11px' }}
                                                >
                                                    <option value="tags">Tag Picker</option>
                                                    <option value="checkbox">Checkboxes</option>
                                                </Form.Select>
                                            )}
                                            {field.type === 'media' && (
                                                <Form.Select
                                                    size="sm"
                                                    className="mt-1"
                                                    value={field.multiple ? 'gallery' : 'single'}
                                                    onChange={(e) => updateField(index, 'multiple', e.target.value === 'gallery')}
                                                    style={{ fontSize: '11px' }}
                                                >
                                                    <option value="single">Single Image</option>
                                                    <option value="gallery">Gallery</option>
                                                </Form.Select>
                                            )}
                                        </Col>
                                        <Col sm={2} className="d-flex flex-column gap-2">
                                            <Form.Check
                                                type="checkbox"
                                                label="Required"
                                                checked={field.required || false}
                                                onChange={(e) => updateField(index, 'required', e.target.checked)}
                                                style={{ fontSize: '12px' }}
                                            />
                                        </Col>
                                        <Col sm={2} className="text-end">
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => removeField(index)}
                                                disabled={newSchemaFields.length <= 2}
                                            >
                                                <FaTrash />
                                            </Button>
                                        </Col>
                                    </Row>

                                    {/* Options input for select (always) and multiselect with checkbox ui */}
                                    {(field.type === 'select' || (field.type === 'multiselect' && field.ui === 'checkbox')) && (
                                        <div className="mt-2 pt-2 border-top">
                                            <div className="d-flex gap-2 mb-2">
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    placeholder="Add option..."
                                                    value={optionInputs[index] || ''}
                                                    onChange={(e) => setOptionInputs(prev => ({ ...prev, [index]: e.target.value }))}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(index); } }}
                                                    style={{ fontSize: '12px' }}
                                                />
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => addOption(index)}
                                                    style={{ fontSize: '12px', whiteSpace: 'nowrap' }}
                                                >
                                                    <FaPlus />
                                                </Button>
                                            </div>
                                            {(field.options || []).length > 0 ? (
                                                <div className="d-flex flex-wrap gap-1">
                                                    {(field.options || []).map((opt, optIdx) => (
                                                        <Badge
                                                            key={optIdx}
                                                            bg="light"
                                                            text="dark"
                                                            className="d-flex align-items-center gap-1 border"
                                                            style={{ fontSize: '11px', cursor: 'pointer' }}
                                                            onClick={() => removeOption(index, opt)}
                                                        >
                                                            {opt} &times;
                                                        </Badge>
                                                    ))}
                                                </div>
                                            ) : (
                                                <small className="text-muted" style={{ fontSize: '11px' }}>
                                                    No options yet — add at least one
                                                </small>
                                            )}
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        ))}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <div className="d-flex justify-content-end gap-2">
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setShowCreateModal(false);
                                resetCreateForm();
                            }}
                            disabled={actionLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={createSchema}
                            disabled={actionLoading}
                        >
                            {actionLoading ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Creating...
                                </>
                            ) : (
                                'Create Content Type'
                            )}
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* View Schema Modal */}
            <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg" centered scrollable dialogClassName="modern-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {selectedSchema?.display_name}
                        {selectedSchema?.is_system && (
                            <Badge bg="secondary" className="ms-2">System</Badge>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ overflowY: 'auto', maxHeight: '70vh' }}>
                    {selectedSchema && (
                        <>
                            <Row className="mb-3">
                                <Col sm={6}>
                                    <small className="text-muted">Schema ID</small>
                                    <div className="fw-medium">{selectedSchema.schema_id}</div>
                                </Col>
                                <Col sm={6}>
                                    <small className="text-muted">Entry Count</small>
                                    <div className="fw-medium">{selectedSchema.entry_count || 0}</div>
                                </Col>
                            </Row>

                            <h6 className="mb-3">Fields ({selectedSchema.fields?.length || 0})</h6>
                            <Table responsive size="sm" className="mb-0">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Type</th>
                                        <th>Required</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedSchema.fields?.map((field, index) => (
                                        <tr key={index}>
                                            <td><code>{field.name}</code></td>
                                            <td>
                                                <Badge bg="light" text="dark">
                                                    {field.type}
                                                </Badge>
                                            </td>
                                            <td>
                                                {field.required ? (
                                                    <Badge bg="warning" text="dark">Yes</Badge>
                                                ) : (
                                                    <span className="text-muted">No</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowViewModal(false)}>
                        Close
                    </Button>
                    {getSchemaRoute(selectedSchema?.schema_id) && (
                        <Button
                            variant="primary"
                            onClick={() => {
                                setShowViewModal(false);
                                navigate(getSchemaRoute(selectedSchema.schema_id));
                            }}
                        >
                            View Entries <FiArrowRight className="ms-2" />
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered dialogClassName="modern-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Delete Data Type</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Are you sure you want to delete <strong>{selectedSchema?.display_name}</strong>?
                    </p>
                    {selectedSchema?.entry_count > 0 && (
                        <Alert variant="warning">
                            This content type has <strong>{selectedSchema.entry_count}</strong> entr{selectedSchema.entry_count === 1 ? 'y' : 'ies'}. Delete all entries first.
                        </Alert>
                    )}
                    <p className="text-muted small mb-0">
                        This action cannot be undone.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowDeleteModal(false)}
                        disabled={actionLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={deleteSchema}
                        disabled={actionLoading || (selectedSchema?.entry_count > 0)}
                    >
                        {actionLoading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Deleting...
                            </>
                        ) : (
                            'Delete'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default StCmsSchemas;
